/**
 * Helper functions for working with the Office.js API.
 * https://learn.microsoft.com/en-us/office/dev/add-ins/develop/understanding-the-javascript-api-for-office
 *
 * Do not use Office imports - the library is loaded by external script
 */

import noop from 'lodash/noop'

import { matchesPaths } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'

function getAsyncResultError() {
  return {
    status: Office.AsyncResultStatus.Failed,
    value: [],
    error: null,
    asyncContext: undefined,
    diagnostics: undefined,
  } as unknown
}

export function isOfficeAddIn() {
  const path = window.location.pathname

  return (
    matchesPaths(path, [
      ROUTES._officeTaskPane,
      ROUTES._officeReferralConfirmation,
      ROUTES._officeConnectEmailAndCalendar,
    ]) ||
    window.location.pathname === ROUTES.officeCheckAuth() ||
    isEmbeddedAsIFrameInOutlook() ||
    isOfficeDialog()
  )
}

function isEmbeddedAsIFrameInOutlook() {
  return (
    window.self !== window.top &&
    (document.referrer.includes('outlook') || // For when the task pane is initially shown in Outlook
      document.referrer.includes(ROUTES.officeTaskPane())) // For when we're redirected to the task pane after login
  )
}

function isOfficeDialog() {
  try {
    return (
      (window.opener && 'Office' in window.opener) ||
      // Can't rely on window.opener for WebView2 (Microsoft Edge2 Chromium-based)
      window.Microsoft?.Office !== undefined ||
      // Sometimes neither window.opener nor window.Microsoft.Office is defined
      // on Outlook for Mac
      (typeof window?.external === 'object' && 'GetHostInfo' in window.external)
    )
  } catch (_error) {
    // To handle cross-origin security issues when trying to access window.opener
    return false
  }
}

export function isOffice(): boolean {
  return (
    typeof Office !== 'undefined' &&
    typeof Office.context !== 'undefined' &&
    typeof Office.context.host !== 'undefined'
  )
}

export function isOfficeUI(): boolean {
  return isOffice() && typeof Office.context.ui !== 'undefined'
}

export function isOfficeParent(): boolean {
  return (
    isOffice() &&
    isOfficeUI() &&
    typeof Office.context.ui.displayDialogAsync !== 'undefined'
  )
}

export function openOfficeDialog(
  path: string,
  callback?: (asyncResult: Office.AsyncResult<Office.Dialog>) => void,
  options?: Office.DialogOptions,
) {
  const url = window.location.origin + path

  if (!isOfficeUI()) {
    console.error('Office.context.ui is not available, redirecting instead.')
    return window.location.assign(url)
  }

  if (!Office.context.ui.displayDialogAsync) {
    console.error('Office.context.ui.displayDialogAsync is not available.')
    return
  }

  Office.context.ui.displayDialogAsync(
    url,
    {
      ...options,
      width: widthPixelsToPercentage(options?.width || 1250),
      height: heightPixelsToPercentage(options?.height || 1100),
      promptBeforeOpen: false,
    },
    callback || noop,
  )
}

// Office.js doesn't support pixel widths so we need to convert to percentage
function widthPixelsToPercentage(width: number) {
  return Math.floor((width / window.screen.availWidth) * 100)
}

// Office.js doesn't support pixel heights so we need to convert to percentage
function heightPixelsToPercentage(height: number) {
  return Math.floor((height / window.screen.availHeight) * 100)
}

export function getToAsyncPromise(): Promise<
  Office.AsyncResult<Office.EmailAddressDetails[]>
> {
  return new Promise((resolve) => {
    if (typeof Office?.context?.mailbox?.item?.to?.getAsync !== 'function') {
      resolve(
        getAsyncResultError() as Office.AsyncResult<
          Office.EmailAddressDetails[]
        >,
      )
      return
    }

    Office?.context?.mailbox?.item?.to.getAsync((result) => {
      resolve(result)
    })
  })
}

export function getCcAsyncPromise(): Promise<
  Office.AsyncResult<Office.EmailAddressDetails[]>
> {
  return new Promise((resolve) => {
    if (typeof Office?.context?.mailbox?.item?.cc?.getAsync !== 'function') {
      resolve(
        getAsyncResultError() as Office.AsyncResult<
          Office.EmailAddressDetails[]
        >,
      )
      return
    }

    Office?.context?.mailbox?.item?.cc.getAsync((result) => {
      resolve(result)
    })
  })
}

export function getBodyAsyncPromise(): Promise<Office.AsyncResult<string>> {
  return new Promise((resolve) => {
    if (typeof Office?.context?.mailbox?.item?.body?.getAsync !== 'function') {
      resolve(getAsyncResultError() as Office.AsyncResult<string>)
      return
    }

    Office?.context?.mailbox?.item?.body.getAsync(
      Office.CoercionType.Text,
      (result) => {
        resolve(result)
      },
    )
  })
}

export function getSubjectAsyncPromise(): Promise<Office.AsyncResult<string>> {
  return new Promise((resolve) => {
    if (
      typeof Office?.context?.mailbox?.item?.subject?.getAsync !== 'function'
    ) {
      resolve(getAsyncResultError() as Office.AsyncResult<string>)
      return
    }

    Office?.context?.mailbox?.item?.subject.getAsync((result) => {
      resolve(result)
    })
  })
}

/**
 * Gets the Office environment information for diagnostic purposes.
 * Returns a formatted string with host, version, and platform information.
 * @returns Office environment string or null if Office is not available
 */
export function getOfficeEnvironmentInfo(): string | null {
  if (!isOfficeAddIn()) {
    return null
  }

  try {
    const host = Office.context.diagnostics.host || 'Unknown'
    const version = Office.context.diagnostics.version || 'Unknown'
    const platform = Office.context.diagnostics.platform || 'Unknown'

    return `${host} | ${version} | ${platform}`
  } catch (error) {
    console.warn('Failed to get Office environment info:', error)
    return null
  }
}
